import * as Toast from "@radix-ui/react-toast";
import { Pencil, User, Mail, Shield, ShieldOff } from "lucide-react";
import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface UserEditDialogProps {
  id: number;
  currentName: string;
  currentEmail: string;
  currentRole: "BROKER" | "ADMIN";
  currentBlocked: boolean;
  onUserUpdate: () => void;
}

export function UserEditDialog({
  id,
  currentName,
  currentEmail,
  currentRole,
  currentBlocked,
  onUserUpdate,
}: UserEditDialogProps) {
  const [openModel, setOpenModel] = useState(false);
  const [name, setName] = useState(currentName);
  const [email, setEmail] = useState(currentEmail);
  const [role, setRole] = useState(currentRole);
  const [blocked, setBlocked] = useState(currentBlocked);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(false);

  function handleToOpenModel() {
    setOpenModel(true);
    setName(currentName);
    setEmail(currentEmail);
    setRole(currentRole);
    setBlocked(currentBlocked);
  }

  async function handleSaveChanges() {
    const updatedUser = {
      username: name,
      email,
      collaborator: role,
      blocked,
    };

    setLoading(true);
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      const response = await fetch(`${route}/api/users/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedUser),
      });

      if (!response.ok) {
        throw new Error("Failed to update user");
      }
      setOpenModel(false);
      onUserUpdate();
      setToastType("success");
      setToastMessage("Usuário editado com sucesso!");
      setToastOpen(true);
    } catch (error) {
      console.error("Failed to update user:", error);
      setToastOpen(true);
      setToastType("error");
      setToastMessage("Falha ao atualizar o usuário!");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Toast.Provider swipeDirection="right">
        <Dialog open={openModel} onOpenChange={setOpenModel}>
          <DialogTrigger asChild>
            <Button
              onClick={handleToOpenModel}
              className="h-10 rounded-xl border border-blue-300 bg-blue-100 px-4 text-blue-700 transition-all duration-200 hover:bg-blue-200"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl bg-white p-6 shadow-2xl sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-2xl font-bold text-gray-900">
                <User className="h-6 w-6 text-blue-600" />
                Editar Usuário
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Atualize as informações do usuário e clique em salvar.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label
                  htmlFor="name"
                  className="flex items-center gap-2 text-sm font-medium text-gray-700"
                >
                  <User className="h-4 w-4" />
                  Nome
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Digite o nome do usuário"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="flex items-center gap-2 text-sm font-medium text-gray-700"
                >
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Digite o email do usuário"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">
                  Cargo
                </Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    className={`h-12 flex-1 rounded-xl transition-all duration-200 ${
                      role === "ADMIN"
                        ? "bg-blue-600 text-white shadow-lg hover:bg-blue-700"
                        : "border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setRole("ADMIN")}
                  >
                    Administrador
                  </Button>
                  <Button
                    type="button"
                    className={`h-12 flex-1 rounded-xl transition-all duration-200 ${
                      role === "BROKER"
                        ? "bg-green-600 text-white shadow-lg hover:bg-green-700"
                        : "border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setRole("BROKER")}
                  >
                    Corretor
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">
                  Status de Acesso
                </Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    className={`flex h-12 flex-1 items-center justify-center gap-2 rounded-xl transition-all duration-200 ${
                      !blocked
                        ? "bg-green-600 text-white shadow-lg hover:bg-green-700"
                        : "border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setBlocked(false)}
                  >
                    <Shield className="h-4 w-4" />
                    Acesso Liberado
                  </Button>
                  <Button
                    type="button"
                    className={`flex h-12 flex-1 items-center justify-center gap-2 rounded-xl transition-all duration-200 ${
                      blocked
                        ? "bg-red-600 text-white shadow-lg hover:bg-red-700"
                        : "border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setBlocked(true)}
                  >
                    <ShieldOff className="h-4 w-4" />
                    Acesso Bloqueado
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                className="h-12 rounded-xl bg-gray-100 px-6 text-gray-700 hover:bg-gray-200"
                onClick={() => setOpenModel(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleSaveChanges}
                disabled={loading}
                className="h-12 rounded-xl bg-blue-600 px-6 text-white hover:bg-blue-700"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Salvando...
                  </div>
                ) : (
                  "Salvar Alterações"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Toast.Root
          open={toastOpen}
          onOpenChange={setToastOpen}
          className={`
            fixed bottom-4 right-4 rounded-xl border p-4 shadow-lg backdrop-blur-sm
            ${
              toastType === "success"
                ? "border-green-400 bg-green-500/90 text-white"
                : "border-red-400 bg-red-500/90 text-white"
            }
          `}
        >
          <Toast.Description className="font-medium">
            {toastMessage}
          </Toast.Description>
          <Toast.Close className="absolute right-2 top-2 text-white/80 hover:text-white" />
        </Toast.Root>
        <Toast.Viewport />
      </Toast.Provider>
    </div>
  );
}
